import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

const FONT = '"Nunito","Baloo 2",sans-serif';

export default function TopicSessionResultPage() {
  const { tagId, levelName } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);

  useEffect(() => {
    const raw = sessionStorage.getItem('topic_session_result');
    if (raw) {
      try { setResult(JSON.parse(raw)); } catch (_) {}
    }
  }, []);

  if (!result) return (
    <div style={{ padding: 24, fontFamily: FONT, textAlign: 'center' }}>
      <p>Không có kết quả gần đây.</p>
      <button onClick={() => navigate(levelName ? '/dashboard' : `/topics/${tagId}`)} style={btn('#1e40af')}>← Quay lại</button>
    </div>
  );

  const levelMode = !!result.level_mode;
  const score = result.score;
  const correct = result.correct;
  const total = result.total;
  const deltas = result.deltas || [];
  const questions = result.questions || [];
  const tagNameById = Object.fromEntries(questions.map((q) => [q.tag_id, q.tag_name]));

  // Build status delta summary
  const renderDelta = (d) => {
    const before = d.before;
    const after = d.after;
    const name = tagNameById[d.tag_id] || d.tag_id.slice(0, 8);
    const lines = [];
    if (d.tier_unlocked) lines.push({ icon: '🆙', text: `Tier ${before.current_tier} → ${after.current_tier} (mở khóa!)`, color: '#10b981' });
    if (d.newly_mastered) lines.push({ icon: '🏆', text: 'Đã thuộc!', color: '#10b981' });
    if (d.review_due_passed) lines.push({ icon: '✅', text: 'Đã ôn lại OK', color: '#10b981' });
    if (after.status !== before.status) {
      lines.push({ icon: '🔄', text: `${before.status || 'unexplored'} → ${after.status}`, color: '#6b7280' });
    }
    const massDelta = (after.mastery_score - (before.mastery_score || 0)).toFixed(1);
    if (Math.abs(parseFloat(massDelta)) > 0) {
      lines.push({
        icon: parseFloat(massDelta) > 0 ? '📈' : '📉',
        text: `Mastery ${(before.mastery_score || 0).toFixed(1)} → ${after.mastery_score.toFixed(1)} (${parseFloat(massDelta) > 0 ? '+' : ''}${massDelta})`,
        color: parseFloat(massDelta) > 0 ? '#10b981' : '#f59e0b',
      });
    }
    if (lines.length === 0) lines.push({ icon: '⚪', text: 'Chưa có thay đổi đáng kể', color: '#9ca3af' });
    return (
      <div key={d.tag_id} style={{
        padding: 12,
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        marginBottom: 8,
      }}>
        <div style={{ fontWeight: 800, color: '#1f2937' }}>{name}</div>
        {lines.map((l, i) => (
          <div key={i} style={{ marginTop: 4, fontSize: 13, color: l.color }}>
            {l.icon} {l.text}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ padding: 24, fontFamily: FONT, maxWidth: 700, margin: '0 auto' }}>
      <div style={{
        background: 'linear-gradient(135deg, #fde68a 0%, #fbbf24 100%)',
        borderRadius: 18, padding: 32, textAlign: 'center', marginBottom: 18,
      }}>
        <div style={{ fontSize: 48 }}>🎉</div>
        <h1 style={{ fontSize: 28, fontWeight: 800, color: '#78350f', margin: '8px 0 4px' }}>
          Hoàn tất!
        </h1>
        <div style={{ fontSize: 48, fontWeight: 800, color: '#78350f' }}>
          {correct} / {total}
        </div>
        <div style={{ fontSize: 18, color: '#92400e', fontWeight: 700, marginTop: 4 }}>
          {score >= 80 ? 'Tuyệt vời!' : score >= 60 ? 'Tốt lắm!' : 'Cố gắng thêm nhé!'}
        </div>
        {levelMode && (
          <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            {result.words_practiced > 0 && (
              <span style={chip('#fff7ed', '#9a3412')}>📚 {result.words_practiced} từ đã luyện</span>
            )}
            {result.stars_earned > 0 && (
              <span style={chip('#fefce8', '#a16207')}>⭐ +{result.stars_earned} sao</span>
            )}
          </div>
        )}
      </div>

      <h2 style={{ fontSize: 18, fontWeight: 700, color: '#374151', marginBottom: 10 }}>
        🌟 Tiến độ từng từ
      </h2>
      <div>{deltas.map(renderDelta)}</div>

      <div style={{ display: 'flex', gap: 10, marginTop: 24, flexWrap: 'wrap' }}>
        <button
          onClick={() => {
            sessionStorage.removeItem('topic_session_result');
            navigate(levelMode ? `/vocab/${levelName}/play` : `/topics/${tagId}/play`);
          }}
          style={btn('#10b981', true)}
        >
          ▶ Học tiếp
        </button>
        {!levelMode && (
          <button onClick={() => { sessionStorage.removeItem('topic_session_result'); navigate(`/topics/${tagId}`); }} style={btn('#1e40af')}>
            Quay lại {result.parent_topic_name}
          </button>
        )}
        {levelMode && (
          <button onClick={() => { sessionStorage.removeItem('topic_session_result'); navigate(`/vocab/words?level=${levelName}`); }} style={btn('#5AC0B5')}>
            📖 Xem sổ từ vựng
          </button>
        )}
        <button onClick={() => { sessionStorage.removeItem('topic_session_result'); navigate('/dashboard'); }} style={btn('#6b7280')}>
          {levelMode ? '🏠 Về trang chủ' : 'Dashboard'}
        </button>
      </div>
    </div>
  );
}

function chip(bg, color) {
  return {
    padding: '6px 14px', background: bg, color,
    borderRadius: 999, fontSize: 14, fontWeight: 800, fontFamily: FONT,
    border: '1px solid rgba(0,0,0,0.06)',
  };
}

function btn(bg, big = false) {
  return {
    padding: big ? '14px 24px' : '12px 20px',
    background: bg,
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontSize: big ? 16 : 14,
    fontWeight: 800,
    cursor: 'pointer',
    fontFamily: FONT,
  };
}
